import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COOKIE_TEXT } from "../i18n/legalContent";

const KEY = "mysymp_consent";

const applyConsent = (v) => {
  const ph = typeof window !== "undefined" ? window.posthog : null;
  if (!ph) return;
  try {
    if (v === "all" && ph.opt_in_capturing) ph.opt_in_capturing();
    else if (ph.opt_out_capturing) ph.opt_out_capturing();
  } catch (e) {
    /* posthog not ready — ignored */
  }
};

const CookieConsent = () => {
  const { lang } = useLang();
  const c = COOKIE_TEXT[lang] || COOKIE_TEXT.de;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (stored) applyConsent(stored);
    else setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener("mysymp:open-cookie-settings", reopen);
    return () => window.removeEventListener("mysymp:open-cookie-settings", reopen);
  }, []);

  const choose = (v) => {
    localStorage.setItem(KEY, v);
    applyConsent(v);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 130, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 130, opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-champagne/20 bg-obsidian/95 backdrop-blur-xl"
          data-testid="cookie-consent"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-5 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-12">
            <div className="flex max-w-2xl items-start gap-4">
              <Cookie size={22} className="mt-1 hidden shrink-0 text-champagne sm:block" strokeWidth={1.4} />
              <div>
                <h3 className="font-serif text-lg font-light text-white">{c.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-white/60">
                  {c.text}{" "}
                  <Link to="/datenschutz" data-testid="cookie-privacy-link" className="text-champagne underline-offset-2 hover:underline">
                    {c.more}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex shrink-0 gap-3">
              <button
                data-testid="cookie-essential-btn"
                onClick={() => choose("essential")}
                className="border border-white/25 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white/80 transition-colors duration-300 hover:border-white hover:text-white"
              >
                {c.essential}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => choose("all")}
                className="bg-champagne px-6 py-3 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
              >
                {c.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
